<!--
  Finish: what happened, and Done.

  "Still got gas" belongs to phase 2 - it has to know which pattern is
  undertrained, and that is a SQL question nobody has answered yet.
-->
<script lang="ts">
  import { app } from '../lib/state.svelte';
  import { duration, kg } from '../lib/format';

  const session = $derived(app.session);
  const done = $derived(app.logged.filter((s) => s.completed));
  const skipped = $derived(app.logged.filter((s) => !s.completed));

  const volume = $derived(
    done.reduce((acc, s) => acc + (s.weight_kg != null && s.reps != null ? s.weight_kg * s.reps : 0), 0),
  );

  const byExercise = $derived.by(() => {
    const groups = new Map<string, { name: string; sets: typeof done }>();
    for (const s of done) {
      const name = app.exerciseById.get(s.exercise_id)?.name ?? 'Unknown';
      if (!groups.has(s.exercise_id)) groups.set(s.exercise_id, { name, sets: [] });
      groups.get(s.exercise_id)!.sets.push(s);
    }
    return [...groups.values()];
  });

  let finishing = $state(false);

  async function done_() {
    finishing = true;
    await app.finish();
    await app.leaveSession();
    finishing = false;
  }
</script>

<div class="finish">
  <main>
    <h2>{done.length ? 'Logged' : 'Nothing logged'}</h2>

    {#if done.length}
      <p class="summary">
        {done.length} sets
        {#if volume > 0}· {kg(volume)} kg moved{/if}
        {#if session}· {duration((Date.now() - new Date(session.started_at).getTime()) / 1000)}{/if}
      </p>

      <ul class="lines">
        {#each byExercise as group (group.name)}
          <li>
            <span class="name">{group.name}</span>
            <span class="detail">
              {#each group.sets as s, i (s.id)}{i > 0 ? ', ' : ''}{s.time_seconds != null
                  ? `${s.time_seconds}s`
                  : `${s.reps ?? '-'}${s.weight_kg != null ? `×${kg(s.weight_kg)}` : ''}`}{/each}
            </span>
          </li>
        {/each}
      </ul>
    {:else}
      <!-- A ghost session. It gets recorded as one: it is the earliest warning
           that things are drifting again, and it costs one boolean. -->
      <p class="summary">Recorded as an opened-and-nothing-logged session.</p>
    {/if}

    {#if skipped.length}
      <p class="skipped">{skipped.length} skipped</p>
    {/if}
  </main>

  <footer>
    <button class="primary" onclick={done_} disabled={finishing}>
      {finishing ? 'Saving…' : 'Done'}
    </button>
    {#if app.pending > 0}
      <p class="pending">{app.pending} waiting to sync - safe on the phone</p>
    {/if}
  </footer>
</div>

<style>
  .finish {
    display: flex;
    flex-direction: column;
    min-height: 100dvh;
    padding: env(safe-area-inset-top) 16px calc(env(safe-area-inset-bottom) + 24px);
  }

  main {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  h2 {
    font-size: 1.6rem;
    margin: 0 0 6px;
  }

  .summary {
    color: var(--dim);
    margin: 0 0 24px;
  }

  .lines {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .lines li {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    border-bottom: 1px solid var(--line);
    padding-bottom: 10px;
  }

  .name {
    font-weight: 500;
  }

  .detail {
    color: var(--dim);
    font-variant-numeric: tabular-nums;
    text-align: right;
    font-size: 0.9rem;
  }

  .skipped {
    color: var(--dim);
    font-size: 0.9rem;
    margin-top: 18px;
  }

  .primary {
    width: 100%;
    min-height: 68px;
    border: 0;
    border-radius: 14px;
    background: var(--accent);
    color: var(--on-accent);
    font-size: 1.1rem;
    font-weight: 600;
  }

  .pending {
    color: var(--dim);
    font-size: 0.85rem;
    text-align: center;
    margin: 10px 0 0;
  }
</style>
